import { useState } from "react";
import feBraintree from "braintree-web";
import { API, API_URL, CONVERT_API, COUNTRY_CODE_API } from "../../constants/config";
import useApi from "./useApi";
import useNotify from "./useNotify";
import content from "../../constants/content";

const useBraintree = () => {
    const { get } = useApi();
    const { warn, error } = useNotify();
    const [inputFieldsInstance, setInputFieldsInstance] = useState(null);
    const [threeDSecure, setThreeDSecure] = useState(null);

    const convertPayment = (amount, code) => {
        const apiUrl = `${CONVERT_API}?a=${amount}&c=${code}`;
        return new Promise((resolve, reject) => {
            get(apiUrl, {}, false).then((res) => {
                if (res && res.success) {
                    resolve(res);
                } else {
                    reject(res);
                }
            });
        })
    }

    const getLocation = () => {
        const apiUrl = COUNTRY_CODE_API;
        return new Promise((resolve) => {
            const onSuccess = (res) => {
                resolve(res);
            };
            get(apiUrl, {}, false, onSuccess);
        });
    };


    const validateInput = () => {
        const state = inputFieldsInstance.getState();
        const { fields } = state;
        const isEmptyField = Object.values(fields).some((field) => {
            if (field.isEmpty) {
                const str = field.container.id;
                const formatted = str
                    .replace("-editor", "")
                    .split("-")
                    .map(word => word.toLowerCase() === "cvv"
                        ? "CVV"
                        : word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(" ");
                warn(`${formatted} is empty`);
                return true;
            }
            return false;
        });
        return isEmptyField;
    }

    const closeIframe = () => {
        const modal = document.getElementById("Cardinal-Modal");
        const frame = document.getElementById("iframe");

        // Remove modal if it exists
        modal?.remove();
        // Remove iframe if it exists
        frame?.remove();
    };

    const getError = (e) => {
        let errorMsg = 'Something went wrong.'
        try {
            errorMsg = e.details.originalError.details.originalError.error.message;
        } catch {
            errorMsg = e.details.originalError.code;
            errorMsg += `: ${e.details.originalError.description}`;
        }
        if (errorMsg.includes('1050')) {
            errorMsg = 'Card authentication failed, your money is not deducted. Please try again with different payment mode.'
        } else if (errorMsg.includes('Unsupported card type')) {
            errorMsg = 'Your card is not supported. Please try again with different payment mode.'
        }
        return errorMsg
    }

    const verifyThreeDSecure = (nonce, price, binData) => {
        return new Promise((resolve, reject) => {
            try {
                const my3DSContainer = document.createElement('div');
                threeDSecure.verifyCard({
                    amount: `${price}`,
                    nonce,
                    bin: binData,
                    onLookupComplete(data) {
                        threeDSecure.initializeChallengeWithLookupResponse(data).then((res) => {
                            const { enrolled } = res && res.threeDSecureInfo || "N";
                            if (enrolled === "Y") {
                                if (res && res.liabilityShifted) {
                                    // 3D Secure verification succeeded, use the 3D Secure nonce for payment
                                    resolve(res);
                                } else {
                                    // Handle case where liability is not shifted
                                    threeDSecure.cancelVerifyCard((err) => {
                                        if (err) {
                                            error(getError(err));
                                        } else {
                                            warn(content.CARD_AUTH_FAILED);
                                        }
                                        reject(err);
                                    });
                                }
                            } else {
                                // Liability not shifted or no 3DS needed
                                resolve(res);
                            }
                            closeIframe();
                        }).catch((err) => {
                            closeIframe();
                            error(getError(err));
                            reject(err);
                        });
                    },
                    addFrame(err, iframe) {
                        // Set up your UI and add the iframe.
                        my3DSContainer.appendChild(iframe);
                        document.body.appendChild(my3DSContainer);
                    },
                    removeFrame() {
                        // Remove UI that you added in addFrame.
                        document.body.removeChild(my3DSContainer);
                    }
                }, (err, res) => {
                    if (err) {
                        closeIframe();
                        error(getError(err));
                        reject(err);
                    }
                    closeIframe();
                    resolve(res);
                });

            } catch (err) {
                warn(content.SOMETHING_WENT_WRONG);
                reject(err);
            }
        });
    };

    const changeCard = (amount) => {
        return new Promise((resolve, reject) => {
            const isEmptyInput = validateInput(inputFieldsInstance);
            if (isEmptyInput) {
                reject();
                return;
            }

            inputFieldsInstance.tokenize((err, payload) => {
                if (err) {
                    warn(`Please give a valid ${err.details.invalidFieldKeys[0]} Information`);
                    reject(err);
                    return;
                }

                const bd = payload.details.bin;
                const newNonce = payload.nonce;

                verifyThreeDSecure(newNonce, amount, bd).then((res) => {
                    return resolve(res)
                }).catch((err) => {
                    reject(err)
                });
            });
        });
    };

    const initializeInputFields = (clientInstance) => {
        let hostedFields;
        let threeDSecure;

        if (typeof feBraintree === "function") {
            hostedFields = feBraintree().hostedFields;
            threeDSecure = feBraintree().threeDSecure;
        } else {
            hostedFields = feBraintree.hostedFields;
            threeDSecure = feBraintree.threeDSecure;
        }

        const hostedFieldsPromise = new Promise((resolve, reject) => {
            hostedFields.create(
                {
                    client: clientInstance,
                    styles: {
                        input: {
                            fontSize: "14px",
                            fontFamily: "Arial, sans-serif",
                            color: "#333333",
                            padding: "10px",
                            border: "1px solid #CCCCCC",
                            borderRadius: "4px",
                            boxShadow: "inset 0 1px 1px rgba(0, 0, 0, 0.075)",
                            width: "100%",
                            boxSizing: "border-box",
                        },
                        "input.invalid": {
                            color: "red",
                        },
                        "input.valid": {
                            color: "green",
                        },
                    },
                    fields: {
                        number: {
                            container: "#card-number-editor",
                            placeholder: "Add Card Number",
                        },
                        cvv: {
                            container: "#cvv-editor",
                            placeholder: "CVV",
                        },
                        expirationDate: {
                            container: "#expiration-date-editor",
                            placeholder: "MM/YY",
                        },
                        postalCode: {
                            container: "#zipcode-editor",
                            placeholder: "Add Zipcode",
                        },
                    },
                },
                (err, hostedFieldsInstance) => {
                    if (err) {
                        reject(err);
                    } else {
                        setInputFieldsInstance(hostedFieldsInstance);
                        resolve(hostedFieldsInstance);
                    }
                }
            );
        });

        const threeDSecurePromise = new Promise((resolve, reject) => {
            threeDSecure.create(
                {
                    client: clientInstance,
                    version: 2,
                },
                (err, threeDSecureInstance) => {
                    if (err) {
                        reject(err);
                    } else {
                        setThreeDSecure(threeDSecureInstance);
                        resolve(threeDSecureInstance);
                    }
                }
            );
        });

        return Promise.all([hostedFieldsPromise, threeDSecurePromise]);
    };

    const createClientToken = () => {
        let client;
        if (typeof feBraintree === "function") {
            client = feBraintree().client;
        } else {
            client = feBraintree.client;
        }

        return new Promise((resolve, reject) => {
            // API call to get the client token
            get(`${API_URL}${API.GET_CLIENT_TOKEN}`, {}, false).then((res => {
                if (!res.error && res.client_token) {
                    client.create(
                        {
                            authorization: res.client_token,
                        },
                        (err, clientInstance) => {
                            if (err) {
                                reject(err); // Reject promise if error
                            }

                            const initializeFields = () =>
                                initializeInputFields(clientInstance)
                                    .then(([hostedFieldsInstance, threeDSecureInstance]) => {

                                        resolve({
                                            hostedFieldsInstance,
                                            threeDSecureInstance,
                                        });
                                    })
                                    .catch((err) => {
                                        reject(err); // Reject if initialization fails
                                    });

                            if (inputFieldsInstance) {
                                inputFieldsInstance.teardown(() => {
                                    initializeFields();
                                });
                            } else {
                                initializeFields();
                            }
                        }
                    );
                } else {
                    reject(new Error("Failed to get client token"));
                }
            })).catch(() => {
                reject(new Error(content.SOMETHING_WENT_WRONG));
            })
        });
    };

    return {
        createClientToken,
        convertPayment,
        changeCard,
        verifyThreeDSecure,
        getLocation
    };
};

export default useBraintree;
